import { routeMappings, apiUrls, SessionStore } from '../../constants';
import axios from '../../common/utils/interceptor';
import { SaveProcessDPNData } from '../../dpn/interfaces';
import { RegisterApiPayload } from '../interfaces/registerApi';
import { getExperienceInitCookie } from './experienceInitCookie';
import { generateApiSignature } from './generateApiSignature';
import { CompanySettings } from '../interfaces/companySettings';
import { TemplateType } from '../../store/interfaces';
import { Products, Task, TaskItem } from '../../taskList/interfaces/taskTile';

interface RegisterResponse {
  status: number;
  message: string;
  customResponseCode: number | null;
}

const psuedoResponse: RegisterResponse = {
  status: 0,
  message: '',
  customResponseCode: null,
};

const formatTasks = (tasks: Task[]): Products[] => {
  const products: Products[] = [];
  tasks.forEach((task: Task) => {
    task.items.forEach((item: TaskItem) => {
      if (item.moduleIds?.length) products.push({ productId: item.id, moduleIds: item.moduleIds });
    });
  });
  return products;
};

export const registerAssessmentTCPlus = async (
  resetIndicator?: boolean,
  autoLogin?: boolean,
): Promise<RegisterResponse> => {
  const companySettingsString = sessionStorage.getItem(SessionStore.companySettings);
  const workflowSession = sessionStorage.getItem(SessionStore.workflowSession);
  const workflowSessionObj = workflowSession && JSON.parse(workflowSession);
  let companySettings: CompanySettings | null = null;
  if (companySettingsString) {
    companySettings = JSON.parse(companySettingsString);
  }
  const isDPNEnabled = companySettings && companySettings.showDpn;
  const isAppFormEnabled = companySettings && companySettings.showAppform;
  const isCandidateRegistered = workflowSessionObj && workflowSessionObj.candidateRegistered;
  const TCPlusRegisterURL = apiUrls.REGISTER_ASSESSMENT_AMCAT;
  const TCPlusRegisterMethod = 'POST';
  if (!isCandidateRegistered) {
    if (
      ((window.location.href.includes(routeMappings['LOGIN']) || autoLogin) && (isDPNEnabled || isAppFormEnabled)) ||
      (window.location.href.includes(routeMappings['DPN']) && isAppFormEnabled && !resetIndicator)
    ) {
      return psuedoResponse;
    } else {
      let dpnData: SaveProcessDPNData = {
        agreedForContact: false,
        dpnId: 0,
      };

      const appFormData = sessionStorage.getItem(SessionStore.applicationFormData);
      const appFormJSON = appFormData && JSON.parse(appFormData);
      if (isDPNEnabled) {
        const dpnSessionData = sessionStorage.getItem(SessionStore.dpnData);
        dpnData = dpnSessionData ? JSON.parse(dpnSessionData) : dpnData;
      }

      const templateString = sessionStorage.getItem(SessionStore.template);
      const template = templateString && JSON.parse(templateString);
      const dpnAccepted = sessionStorage.getItem(SessionStore.dpnAccepted);
      const experienceProps = getExperienceInitCookie();

      const authStream = sessionStorage.getItem(SessionStore.tcPlusAuthStream);
      const basicTemplate = template.type === TemplateType.BASIC_EXPERIENCE_LITE;

      const candidateData = sessionStorage.getItem(SessionStore.candidate);
      const candidateEmail = candidateData && JSON.parse(candidateData).email;
      const defaultFormData: { [key: string]: string } = {};
      defaultFormData['emailId'] = candidateEmail;
      const registerApiData: { [key: string]: string } = {
        isDpnAccepted: dpnAccepted === 'true' ? '1' : '0',
        location: '{}',
        formdata: appFormJSON
          ? JSON.stringify({ ...appFormJSON, emailId: candidateEmail })
          : JSON.stringify(defaultFormData),
        securityQuestion: '{"questionId":"1","answer":"demo"}',
        dpnMetadata: JSON.stringify(dpnData) || '{}',
      };

      let apiToken = '';
      let apiSecret = '';
      if (authStream != null) {
        apiSecret = JSON.parse(authStream).apiSecret;
        apiToken = JSON.parse(authStream).apiToken;
      }

      const apiSignature = generateApiSignature(
        apiToken,
        apiSecret,
        registerApiData,
        TCPlusRegisterURL,
        TCPlusRegisterMethod,
      );

      const payloadData: RegisterApiPayload = {
        templateId: template?.id || '',
        dpnData: dpnData,
        appFormData: appFormJSON ? { ...appFormJSON, emailId: candidateEmail } : defaultFormData,
        companyUserId: experienceProps && experienceProps.companyUserId,
        dpnAccepted: dpnAccepted === 'true' ? 1 : 0,
        apiAuthToken: apiToken,
        apiSignature: apiSignature,
      };
      if (basicTemplate) {
        const basicTaskList = sessionStorage.getItem(SessionStore.basicTaskList);
        if (basicTaskList) {
          const tasks: Task[] = basicTaskList && JSON.parse(basicTaskList);
          payloadData.products = formatTasks(tasks);
        }
        sessionStorage.removeItem(SessionStore.basicTaskList);
      }
      if (appFormData) payloadData.viaAppForm = true;
      try {
        const { status, data } = await axios.post(apiUrls.POST_ASSESSMENT_REGISTER, payloadData);
        data.status = status;
        return data;
      } catch (e) {
        const responseData: { status: number; message: string; customResponseCode: number | null } = {
          status: 200,
          message: '',
          customResponseCode: null,
        };
        responseData.status = e.status;
        if (e.status === 400) {
          responseData.message = e.data.message;
          responseData.customResponseCode = e.data.customResponseCode || null;
        } else {
          responseData.message = 'genericError';
        }
        return responseData;
      }
    }
  }

  return psuedoResponse;
};
